import {CurrentUserService} from "../../services/user/CurrentUserService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {NavigationModel, SidebarNavModel} from "../models/NavigationModel";
import {CurrentUser} from "../../services/user/models/CurrentUser";
import {BreakpointObserver} from "../../services/breakpoints/BreakpointObserver";
import {
    achievementsNavItem,
    adminPanelNavItem,
    adviserNavItem,
    auditLogNavItem,
    backtestingNavItem,
    chartingNavItem,
    clearingDashboardNavItem,
    companyInfoNavItem,
    education, gameTradeHistoryNavItem,
    hedgeHelperNavItem,
    homeNavItem,
    NavigationItem,
    NavigationItems,
    ordersNavItem,
    portfolioNavItem,
    positionSearchNavItem,
    processManagementNavItem,
    riskManagerNavItem,
    stockScreenerNavItem,
    strategyBuilderNavItem,
    supportManagerNavItem,
    tradeHistoryNavItem,
    tradingDashboardNavItem
} from "../models/NavigationItems";
import {FeatureNames} from "../../types/UserConfig";
import {Breakpoint} from "../../services/breakpoints/Breakpoint";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {LocalStorageService} from "../../types/NgStorage";
import {SidenavState} from "../models/SidenavState";
import {AccountStore} from "../../account/store/AccountStore";
import {isNotNull} from "../../js/util/UtilFunctions";
import {Branding, isOpenMarketsAppBranding} from "../../types/Branding";
import NavItemSorting from "./NavItemSorting";

const SidebarBreakpoint = Breakpoint.WebLandscape;

const OldSidebarStateStorageKey = "menuType";
type OldSidenavStates = "new" | "old";
const NewSidebarStorageKey = "sidenav-state";

export class NavigationModelService {

    public static readonly id = "navigationModelService";

    private readonly topBarModel$ = new ReplaySubject<NavigationModel>(1);
    private readonly sideBarModel$ = new ReplaySubject<SidebarNavModel>(1);

    private readonly isMobileNav$ = new BehaviorSubject(false);
    private readonly sidebarVisible$ = new BehaviorSubject(false);
    private readonly sidebarOpen$ = new BehaviorSubject(false);
    private readonly userMenuOpen$ = new BehaviorSubject(false);
    private readonly sidebarPinned$ = new BehaviorSubject(false);

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly accountStore: AccountStore,
                       private readonly breakpointObserver: BreakpointObserver,
                       private readonly systemEventService: SystemEventService,
                       private readonly $localStorage: LocalStorageService) {
        this.init();
    }

    private init() {
        const sidebarBreakpointActive$ = this.breakpointObserver
                                             .observe(SidebarBreakpoint)
                                             .map((breakpoint) => breakpoint.active)
                                             .map((active) => !active) // Show sidebar when the breakpoint isn't active
                                             .observeOn(Scheduler.async);

        const isClientUser$ = this.currentUserService.current.map((user) => user.isClient());
        const isAdviser$ = this.currentUserService.current.map((user) => user.isAdviser());
        const isAdmin$ = this.currentUserService.current.map((user) => user.isAdmin());

        this.watchSidebarVisibility(sidebarBreakpointActive$, isAdviser$, isAdmin$);
        this.watchTopBarNavModel(sidebarBreakpointActive$);
        this.watchSidebarModel(isClientUser$);
        sidebarBreakpointActive$.subscribe((active) => this.isMobileNav$.next(active));
        this.sidebarPinned.subscribe((pinned) => {
            if (pinned) {
                this.$localStorage[NewSidebarStorageKey] = SidenavState.PINNED;
            } else {
                this.$localStorage[NewSidebarStorageKey] = SidenavState.FLOATING;
            }
            this.systemEventService.windowResize();
        });
        this.initAndUpgradeSidenavState();
    }

    private initAndUpgradeSidenavState() {
        const newStateData: SidenavState | null = this.$localStorage[NewSidebarStorageKey];
        if (newStateData === SidenavState.FLOATING) {
            this.unpinSidebar();
        } else if (newStateData === SidenavState.PINNED) {
            this.pinSidebar();
        } else {
            const oldStateData: OldSidenavStates | null = this.$localStorage[OldSidebarStateStorageKey];
            if (oldStateData === "new") {
                this.unpinSidebar();
            } else if (oldStateData === "old") {
                this.pinSidebar();
            }
        }
    }

    private watchSidebarVisibility(sidebarBreakpoint$: Observable<boolean>,
                                   adviserUser$: Observable<boolean>,
                                   adminUser$: Observable<boolean>) {
        Observable.combineLatest(sidebarBreakpoint$, adviserUser$, adminUser$)
                  .map(([breakpointActive, isAdviser, isAdmin]) => breakpointActive || isAdviser || isAdmin)
                  .subscribe((sidebarVisible) => this.sidebarVisible$.next(sidebarVisible));
    }

    private watchTopBarNavModel(sidebarBreakpointActive$: Observable<boolean>) {
        const navItems$ = this.topBarNavItems();

        Observable.combineLatest(navItems$, sidebarBreakpointActive$)
                  .map(([navigationItems, sidebarBreakpointActive]) => {
                      if (sidebarBreakpointActive) {
                          return {
                              navigationItems: []
                          };
                      } else {
                          return {
                              navigationItems
                          };
                      }
                  })
                  .subscribe((model) => this.topBarModel$.next(model));
    }

    private watchSidebarModel(isClientUser$: Observable<boolean>) {
        const navItems$ = this.sideBarNavItems();
        Observable.combineLatest(navItems$, isClientUser$)
                  .map(([items, isClient]) => this.sidebarModel(items, isClient))
                  .subscribe((model) => this.sideBarModel$.next(model));
    }

    private sidebarModel(items: NavigationItems,
                         isClient: boolean): SidebarNavModel {
        return {
            navigationItems: items,
            showFavourites: !isClient
        };
    }

    private topBarNavItems(): Observable<NavigationItems> {
        const portfolioItem$ = this.portfolioNavigationItem();
        const tradeHistoryItem$ = this.tradeHistoryNavigationItem();
        const gameTradeHistoryItem$ = this.gameTradeHistoryNavigationItem();
        const ordersItem$ = this.ordersNavItem();
        const currentUser$ = this.currentUserService.current;
        return Observable.combineLatest(currentUser$, portfolioItem$, tradeHistoryItem$, gameTradeHistoryItem$, ordersItem$)
                         .map(([user, portfolioItem, historyItem, gameHistoryItem, ordersItem]) => {
                             const items = this.userTopNavItems(user, portfolioItem, historyItem, gameHistoryItem, ordersItem);
                             return NavItemSorting(user.branding(), items);
                         });
    }

    private userTopNavItems(user: CurrentUser,
                            portfolioItem: NavigationItem | null,
                            historyItem: NavigationItem | null,
                            gameHistoryItem: NavigationItem | null,
                            ordersItem: NavigationItem | null): NavigationItems {
        const items: Array<NavigationItem> = [];

        if (user.branding() !== Branding.TC &&
            user.branding() !== Branding.ALLFIN &&
            user.branding() !== Branding.WAIMAK) {
            items.push(homeNavItem());
        }

        if (user.branding() === Branding.IV) {
            items.push(tradingDashboardNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_COMPANY_INFO)) {
            items.push(companyInfoNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_STRATEGY_BUILDER)) {
            items.push(strategyBuilderNavItem());
        }
        if (portfolioItem != null) {
            items.push(portfolioItem);
        }
        if (historyItem != null && !user.isGame()) {
            items.push(historyItem);
        }
        if (gameHistoryItem != null && user.isGame()) {
            items.push(gameHistoryItem);
        }
        if (isOpenMarketsAppBranding(user.branding()) && user.branding() !== Branding.WAIMAK) {
            items.push(stockScreenerNavItem());
        }
        if (user.hasFeature(FeatureNames.FEATURE_ADV_CHARTING)) {
            items.push(chartingNavItem());
        }
        if (user.hasFeature(FeatureNames.SCREEN_BACKTESTING)) {
            items.push(backtestingNavItem());
        }
        if (ordersItem != null) {
            items.push(ordersNavItem());
        }
        if (user.isGame()) {
            items.push(achievementsNavItem());
        }
        if (user.isIv()) {
            items.push(education());
        }

        if (user.isBranding(Branding.ALLFIN) && user.isClient()) {
            items.push(tradingDashboardNavItem());
        }

        return items;
    }

    private portfolioNavigationItem(): Observable<NavigationItem | null> {
        const source$ = Observable.combineLatest(this.accountStore.current, this.currentUserService.current);
        return source$
                .filter(([_, user]) => {
                    return user.isClient() || (!user.isClient() &&  user.isOma());
                })
                      .map(([account]) => account)
                      .filter(isNotNull)
                      .map((account) => account.id)
                      .map((id) => portfolioNavItem(id))
                      .startWith(null);
    }

    private tradeHistoryNavigationItem(): Observable<NavigationItem | null> {
        return Observable.combineLatest(this.accountStore.current, this.currentUserService.current)
                         .observeOn(Scheduler.async)
                         .filter(([_, currentUser]) => currentUser.hasFeature(FeatureNames.FEATURE_ACCOUNT_PERFORMANCE) || currentUser.isAdmin() || !currentUser.isGame())
                         .map(([account, _]) => account)
                         .filter(isNotNull)
                         .map((account) => account.id)
                         .map((id) => tradeHistoryNavItem(id))
                         .startWith(null);
    }

    private gameTradeHistoryNavigationItem(): Observable<NavigationItem | null> {
        return Observable.combineLatest(this.accountStore.current, this.currentUserService.current)
                         .observeOn(Scheduler.async)
                         .filter(([_, currentUser]) => currentUser.hasFeature(FeatureNames.FEATURE_ACCOUNT_PERFORMANCE) || currentUser.isAdmin() || currentUser.isGame())
                         .map(([account, _]) => account)
                         .filter(isNotNull)
                         .map((account) => account.id)
                         .map((id) => gameTradeHistoryNavItem(id))
                         .startWith(null);
    }

    private ordersNavItem(): Observable<NavigationItem | null> {
        return this.currentUserService
                   .current
                   .map((user) => user.viewOrdersEnabled)
                   .map((enabled) => enabled ? ordersNavItem() : null);
    }

    private sideBarNavItems(): Observable<NavigationItems> {
        const portfolioItem$ = this.portfolioNavigationItem();
        const tradeHistoryItem$ = this.tradeHistoryNavigationItem();
        const gameTradeHistoryItem$ = this.gameTradeHistoryNavigationItem();
        const ordersItem$ = this.ordersNavItem();
        const currentUser$ = this.currentUserService.current;
        return Observable.combineLatest(currentUser$, portfolioItem$, tradeHistoryItem$, gameTradeHistoryItem$, ordersItem$)
                         .map(([user, portfolioItem, historyItem, gameTradeHistoryItem, ordersItem]) => {
                             const items = this.userSideNavItems(user, portfolioItem, historyItem, gameTradeHistoryItem, ordersItem);
                             return NavItemSorting(user.branding(), items);
                         });
    }

    private userSideNavItems(user: CurrentUser,
                             portfolioItem: NavigationItem | null,
                             historyItem: NavigationItem | null,
                             gameHistoryItem: NavigationItem | null,
                             ordersItem: NavigationItem | null): NavigationItems {
        const items: Array<NavigationItem> = [];

        if (user.branding() !== Branding.TC &&
            user.branding() !== Branding.ALLFIN &&
            user.branding() !== Branding.WAIMAK) {
            items.push(homeNavItem());
        }

        if (user.branding() === Branding.IV) {
            items.push(tradingDashboardNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_STRATEGY_BUILDER)) {
            items.push(strategyBuilderNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_COMPANY_INFO)) {
            items.push(companyInfoNavItem());
        }

        if (portfolioItem != null) {
            items.push(portfolioItem);
        }

        if (historyItem != null) {
            items.push(historyItem);
        }

        if (gameHistoryItem != null) {
            items.push(gameHistoryItem);
        }

        if (user.hasFeature(FeatureNames.SCREEN_ACCOUNT_LIST)) {
            items.push(adviserNavItem());
        }

        if (user.hasAnyFeature(FeatureNames.SCREEN_OPTIONS_POSITIONS_SEARCH,
                               FeatureNames.SCREEN_EQUITY_POSITIONS_SEARCH)) {
            items.push(positionSearchNavItem());
        }

        if (user.isRiskManager()) {
            items.push(riskManagerNavItem());
        }

        if (user.hasFeature(FeatureNames.CLEARING_DASHBOARD)) {
            items.push(clearingDashboardNavItem());
        }

        if (isOpenMarketsAppBranding(user.branding())) {
            items.push(stockScreenerNavItem());
        }

        if (user.hasFeature(FeatureNames.FEATURE_ADV_CHARTING)) {
            items.push(chartingNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_BACKTESTING)) {
            items.push(backtestingNavItem());
        }

        if (ordersItem != null) {
            items.push(ordersNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_SUPPORT_MANAGER)) {
            items.push(auditLogNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_ADMIN)) {
            items.push(adminPanelNavItem());
        }

        if (user.hasFeature(FeatureNames.SCREEN_SUPPORT_MANAGER)) {
            items.push(supportManagerNavItem());
        }

        if (user.isSuperUser() && !user.isTc()) {
            items.push(hedgeHelperNavItem());
        }

        if (user.isSuperUser() && !user.isTc()) {
            items.push(processManagementNavItem());
        }

        if (user.isGame()) {
//             items.push(education());
            items.push(achievementsNavItem());
        }

        if (user.isBranding(Branding.ALLFIN)) {
            items.push(tradingDashboardNavItem());
        }

        return items;
    }

    public get topNavModel(): Observable<NavigationModel> {
        return this.topBarModel$.observeOn(Scheduler.async);
    }

    public get sideNavModel(): Observable<NavigationModel> {
        return this.sideBarModel$.observeOn(Scheduler.async);
    }

    public get sidebarVisible(): Observable<boolean> {
        return this.sidebarVisible$.observeOn(Scheduler.async);
    }

    public get sidebarOpen(): Observable<boolean> {
        return this.sidebarOpen$.observeOn(Scheduler.async);
    }

    public toggleSidebar() {
        const current = this.sidebarOpen$.getValue();
        const updated = !current;
        this.sidebarOpen$.next(updated);
    }

    public openSidebar() {
        this.sidebarOpen$.next(true);
    }

    public closeSidebar() {
        this.sidebarOpen$.next(false);
    }

    public get userMenuOpen(): Observable<boolean> {
        return this.userMenuOpen$.observeOn(Scheduler.async);
    }

    public toggleUserMenu() {
        const current = this.userMenuOpen$.getValue();
        const updated = !current;
        this.userMenuOpen$.next(updated);
    }

    public openUserMenu() {
        this.userMenuOpen$.next(true);
    }

    public closeUserMenu() {
        this.userMenuOpen$.next(false);
    }

    public get sidebarPinned(): Observable<boolean> {
        return this.sidebarPinned$.observeOn(Scheduler.async);
    }

    public toggleSidebarPin() {
        const current = this.sidebarPinned$.getValue();
        const updated = !current;
        this.sidebarPinned$.next(updated);
    }

    public pinSidebar() {
        this.sidebarPinned$.next(true);
    }

    public unpinSidebar() {
        this.sidebarPinned$.next(false);
    }

    public get isMobileNav(): Observable<boolean> {
        return this.isMobileNav$.observeOn(Scheduler.async);
    }
}
